import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { sortBy } from 'underscore';
import { useDispatch } from 'react-redux';

import '../../../../../../scss/live-event/base/modules/agenda.scss';
import './agenda-editor.scss';
import {
	ColorKeys,
	ContentEditors,
	CreateSession,
	ModuleMap,
	PageModuleType,
	Session,
	SessionTypesEnum,
	TextOverrides
} from '../../../../../../types/working-model';
import { AgendaLayoutTypesClassic, TemplateClassNames } from '../../../../../../types/template-layouts';
import { getStylingOverrides } from '../../../../../live-event/utils';
import TabHeader from '../../../../../general-ui/tab-header/tab-header';
import { TypographyItem } from '../../../../../general-ui/typography-item/typography-item';
import { getDefaultAgendaImage } from '../../../../../../store/utils/create-event';
import { Button } from '../../../../../general-ui/button/button';
import { filterSessionsByAgendaModuleSettings, getDefaultModuleAlignment, getTextValue, updateTranslateKey } from '../../../../../../utils/utils';
import SessionCard from './agenda-session-card';
import { LiveSessions } from './live-sessions';
import { OnDemandSessions } from './ondemand-sessions';
import Controls from '../../../session/editor/moduleControls/controls';
import TextInput from '../../../../../general-ui/text-input/text';
import { ICONS } from '../../../../../general-ui/icon';
import EditAgendaCard from './edit-agenda-card';
import { addNewSession, updateHomepageModule, updateSessionInfo, updateWorkingEvent } from '../../../../../../store/actions/admin/create-event';

import SessionModal from '../../../session/session-modal';
import EditableTextSlim from '../../../../../general-ui/editable-text/editable-text-slim';
import { EContentColorOverrideNames } from '../../../../../general-ui/edit-section-colors/edit-section-colors-modal';
import { BasicLangingModuleProps } from '../editor-main';
import EditableTextDraftQuill from '../../../../../general-ui/editable-text/editable-text-draft-quill';
import { useTranslate } from '../../../../../../i18n/useTranslationModules';
import { CalendarLayout } from '../../../../../live-event/modules/agenda/calendar-layout';
import { useThunkDispatch, useTypedSelector } from '../../../../../../store/reducers/use-typed-selector';
import ReorderContentModal from '../../../session/reorder-sessions-modal';
import ManageAgenda from './agenda-session-management';
import useTranslationRoute from '../../../../../live-event/hooks/use-translation-route';
import { useIsNewNavigation } from '../../../../../../hooks/navigation.hooks';
import { useGetLayoutType } from '../../../../../../hooks/agenda.hooks';


function filterBroadcasts(session: Session) {
	return session.session_type === SessionTypesEnum.broadcast;
}

function filterOnDemand(session: Session) {
	return session.session_type === SessionTypesEnum.onDemand;
}

function filterBreakoutRooms(session: Session) {
	return session.session_type === SessionTypesEnum.breakoutRooms;
}

function filterFireside(session: Session) {
	return session.session_type === SessionTypesEnum.fireside;
}

const EditorAgenda: React.FC<BasicLangingModuleProps> = React.forwardRef<HTMLDivElement, BasicLangingModuleProps>((props, ref) => {
	const {
		page_module: module,
		template,
		language,
		baseLanguage,
		isLandingPage,
		isCustomPage,
		isHomePage,
		customPageId,
	} = props;
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const token = useTypedSelector(state => state.AuthReducer.token);
	const user = useTypedSelector(state => state.AuthReducer.user);
	const namespace = useTranslationRoute();
	const { t } = useTranslate(namespace);
	const _sessions = workingEvent?.sessions;
	const [activeTab, setActiveTab] = useState<string>();
	const [addingSession, setAddingSession] = useState<SessionTypesEnum | null>(null);
	const [reorderingSessions, setReorderingSessions] = useState(Boolean);

	const [contentManagement, setContentManagement] = useState<boolean>(false);
	const [searchSessions, setSearchSessions] = useState('');
	const [workingSessions, setWorkingSessions] = useState<Session[] | null>(null);
	const isNewNavigation= useIsNewNavigation();
	const dispatch = useDispatch();
	const thunkDispatch = useThunkDispatch();

	const LIVE_SESSIONS = t(`Live sessions`);
	const ON_DEMAND = t(`On demand sessions`);
	//Because this module is mirroring its published counterpart it needs these variables.
	//We set them to false here so the admins will always be editing the unregistered, non - live version of agenda
	//Keeping the JSX code around in case the editor eventually allows you to see these versions
	const isUpNext = false;
	const isLive = false;

	const { content, styling_overrides } = module;
	
	const layout_type = useGetLayoutType(content?.layout_type);

	//init as one variable so it can be passed to the card
	const stylingOverrides = getStylingOverrides(styling_overrides);
	const {
		backgroundColor,
		backgroundImg,
	} = stylingOverrides;

	const showOnDemandSessions = content?.show_on_demand_sessions ?? true;
	const showBroadcastSessions = content?.show_broadcast_sessions ?? true;

	// filter sessions based on agenda module settings
	const sessions = useMemo(() => {
		return filterSessionsByAgendaModuleSettings({
			sessions: _sessions ?? [],
			showBroadcastSessions,
			showOnDemandSessions,
		});
	}, [_sessions, showOnDemandSessions, showBroadcastSessions]) as Session[];

	const broadcastSessionList = useMemo(
		() => sessions?.filter(filterBroadcasts) || [],
		[sessions]
	);
	const onDemandSessionList = useMemo(
		() => sessions?.filter(filterOnDemand) || [],
		[sessions]
	);
	const breakoutRoomsSessionList = useMemo(
		() => sessions?.filter(filterBreakoutRooms) || [],
		[sessions]
	);
	const firesideSessionList = useMemo(
		() => sessions?.filter(filterFireside) || [],
		[sessions]
	);

	useEffect(() => {
		const interval = setTimeout(() => {
			setActiveTab(LIVE_SESSIONS);
		});
		return () => clearTimeout(interval);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);


	const handleAddNewSession = () => {
		setAddingSession(SessionTypesEnum.broadcast);
	};

	const handleCloseManageAgenda = () => {
		setContentManagement(false);
	};

	function handleTextChange(key: string) {
		return (text: string) => {
			dispatch(
				updateHomepageModule({
					...module,
					content: {
						...content,
						[key]: updateTranslateKey({
							translateString: module?.content?.[key],
							input: text,
							language,
							baseLanguage
						}),
					},
					...(key === 'description' && { is_edited: true }),
				})
			);
		};
	}

	function handleStyleOverrideChange(key: string) {
		return (overrides: TextOverrides) => {
			const updatedOverrides = {
				...styling_overrides,
				content: {
					...styling_overrides?.content,
					[key]: overrides
				}
			};
			dispatch(
				updateHomepageModule({
					...module,
					styling_overrides: updatedOverrides
				})
			);
		};
	}

	const renderSessions = (): JSX.Element | undefined => {
		const liveSessionsExist = !!(broadcastSessionList.length || breakoutRoomsSessionList.length || firesideSessionList.length);
		const onDemandExists = !!onDemandSessionList.length;
		const hasTabs = liveSessionsExist && onDemandExists;
		const liveSessionsList = sortBy([...broadcastSessionList, ...breakoutRoomsSessionList, ...firesideSessionList], "timestamp");

		if (module.content.layout_type === AgendaLayoutTypesClassic.calendar && sessions) {
			return (
				<CalendarLayout
					module={module}
					template={template}
					sessions={sessions}
					language={language}
					editor={true}
				/>
			);
		}

		if (isLive) {
			return (
				<>
					{hasTabs && (
						<TabHeader
							tabs={[LIVE_SESSIONS, ON_DEMAND]}
							onClick={setActiveTab}
							currentTab={activeTab}
							template={template}
							hasDivider={true}
							shouldTransformText={false}
						/>
					)}
					{((!hasTabs || activeTab === LIVE_SESSIONS) && liveSessionsExist) && (
						<LiveSessions
							baseLanguage={baseLanguage}
							language={language}
							module={module}
							sessions={liveSessionsList}
							template={template}
							isLive={isLive}
							isUpNext={isUpNext}
						/>
					)}
					{((!hasTabs || activeTab === ON_DEMAND) && onDemandExists) && (
						<OnDemandSessions
							baseLanguage={baseLanguage}
							language={language}
							module={module}
							sessions={onDemandSessionList}
							template={template}
							isLive={isLive}
						/>
					)}
				</>
			);
		} else {
			return <>
				{liveSessionsExist && (
					<LiveSessions
						baseLanguage={baseLanguage}
						language={language}
						module={module}
						sessions={liveSessionsList}
						template={template}
						isLive={isLive}
						isUpNext={isUpNext}
					/>)}
				{onDemandExists && (
					<OnDemandSessions
						baseLanguage={baseLanguage}
						language={language}
						module={module}
						sessions={onDemandSessionList}
						template={template}
						isLive={isLive}
					/>)}
			</>;
		}
	};

	const visibleAgendas = useMemo(() => {
		if (sessions) {
			const _sessions = workingSessions ? workingSessions.slice() : sessions.slice();
			const _term = searchSessions.replace(/\s/g, '').trim().toLowerCase();
			return _sessions.filter(session => session.title.base.replace(/\s/g, '').toLowerCase().trim().includes(_term));
		} else {
			return [];
		}
	}, [searchSessions, sessions, workingSessions]);

	function toggleVisibleSession(session: number) {
		if (workingEvent?.settings) {
			const _session = workingSessions ? workingSessions.find(s => s.session === session) : sessions.find(s => s.session === session);
			if (_session) {
				_session.is_on = !_session?.is_on;
				setWorkingSessions(workingSessions?.map(s => {
					if (s.session === session) {
						return _session;
					}
					return s;
				}) ?? null);
			}
		}
	}

	function removeVisibleSession(session: number) {
		if (!workingEvent?.settings || !token) return;
		const _sessions = workingSessions ? workingSessions.slice() : sessions.slice();
		const filteredSessions = _sessions.filter(s => s.session !== session);

		thunkDispatch(updateWorkingEvent({
			...workingEvent,
			sessions: filteredSessions
		}, token));
	}

	function handleContentManagement() {
		setContentManagement(true);
	}

	const handleCloseModal = () => setAddingSession(null);



	function createSession(session: CreateSession) {
		return new Promise<void>(resolve => {
			if (!workingEvent || !token || !user) return resolve();

			//if the user didn't pick a language, they get english
			if (session.languages.length === 0) {
				session.languages = ['en'];
				session.default_language = 'en';
			}

			//if there's no default language, make it english if it exists, otherwise pick the first non-english language
			if (!session.default_language) {
				if (session.languages.includes('en')) {
					session.default_language = 'en';
				} else {
					session.default_language = session.languages[0];
				}
			}

			const finishAddingSession = () => {
				setAddingSession(null);
				resolve();
			};

			dispatch(addNewSession(session, Number(workingEvent.event), workingEvent.uuid, user.active_channel, token, finishAddingSession));

		});
	}

	const editAgenda = (
		<div className="edit-content">
			<div className="search">
				<TextInput
					onChange={(e) => setSearchSessions(e.target.value)}
					placeholder="Search sessions to add..."
					icon={ICONS.SEARCH}
					size={"small"}
				/>
				or
				<button className="button small">Create Session</button>
			</div>
			<div className="edit-agenda-cards">
				{
					visibleAgendas.map((session, idx) => {
						return (
							<EditAgendaCard
								key={session.session + idx}
								session={session}
								toggleDisplay={toggleVisibleSession}
								onEdit={() => ('placeholder')}
								onDelete={removeVisibleSession}
							/>
						);
					})
				}
			</div>
		</div>
	);

	const { custom_heading, title, description } = getTextValue(content, language);
	const customHeadingDefaultColor = (template === TemplateClassNames.O2)
		? ColorKeys.secondaryBackgroundColor
		: ColorKeys.secondaryAccentColor;

	if (!module.is_on) return null;
	const controlsDisplay = {
		editColors: true,
		editLayout: true,
		addContent: true,
		changeSectionPosition: true,
		removeSection: false,
		reorderContent: true,
		contentManagement: false,
	};

	return (
		<section
			ref={ref}
			id={ModuleMap[PageModuleType.agenda]}
			className={classNames(
				'editor-wrapper agenda agenda-editor',
				`agenda-${layout_type}`,
				backgroundColor,
				template,
				{ 'new-event': isNewNavigation }
			)}
		>
			<Controls
				module={module}
				contentButtonText="Add session"
				openContentModal={handleAddNewSession}
				openReorderModal={() => { setReorderingSessions(true); }}
				openEditModal={handleContentManagement}
				// openContentManagementModal={handleContentManagement}
				editableContent={{
					jsx: editAgenda,
					onDone: () => workingSessions ? workingSessions.map(s => {
						if (token) dispatch(updateSessionInfo(s, token));
					}) : {},
				}}
				baseLanguage={baseLanguage}
				language={language}
				display={controlsDisplay}
			/>
			<div className="module-container" style={backgroundImg}>
				{
					!isUpNext ?
						<>
							<EditableTextSlim
								tagType="p"
								className="upper-agenda-title"
								value={t(`${namespace}:agenda.${module.id}.heading`, t(`${namespace}:agenda.heading`, custom_heading))}
								defaultColor={customHeadingDefaultColor}
								onChange={handleTextChange('custom_heading')}
								onOverrideChange={handleStyleOverrideChange('custom_heading')}
								elementName={EContentColorOverrideNames.custom_heading}
								colorOverride={styling_overrides?.content?.custom_heading?.color}
							/>
							<EditableTextSlim
								tagType="h2"
								className="agenda-title"
								elementClass="evt-heading-2"
								value={t(`${namespace}:agenda.${module.id}.title`, t(`${namespace}:agenda.title`, title))}
								defaultColor={ColorKeys.bodyTextColor}
								onChange={handleTextChange('title')}
								onOverrideChange={handleStyleOverrideChange('title')}
								elementName={EContentColorOverrideNames.title}
								colorOverride={styling_overrides?.content?.title?.color}
							/>
							<EditableTextDraftQuill
								className="agenda-description"
								value={t(`${namespace}:agenda.${module.id}.description`, t(`${namespace}:agenda.description`, description))}
								onChange={handleTextChange("description")}
								defaultColor={ColorKeys.bodyTextColor}
								onOverrideChange={handleStyleOverrideChange('description')}
								elementName={EContentColorOverrideNames.description}
								styleOverrides={styling_overrides?.content?.description}
								useNew={workingEvent?.settings?.content_editor === ContentEditors.quill}
								defaultTextAlignment={getDefaultModuleAlignment(module, template)}
							/>
						</> :
						null
				}

				<div className={classNames('agenda-featured-container')}>
					{/* TODO: Figure out how we choose the "featured" session */}
					{
						sessions && sessions[0] && <SessionCard
							baseLanguage={baseLanguage}
							language={language}
							key={sessions[0].uuid}
							session={sessions[0]}
							stylingOverrides={stylingOverrides}
							template={template}
							defaultImage={getDefaultAgendaImage(template, 0)}
							featuredCard
						/>
					}
				</div>
				<div className={classNames('agenda-sessions-container')}>
					{
						isUpNext &&
						<div className="agenda-next-sessions-header">
							<TypographyItem className={classNames('evt-heading-2 agenda-title')} tagName="h2">
								{t("Up next")}
							</TypographyItem>
							<Button
								tagName="a"
								url={'#'}
								classButton="tertiary"
								template={template}
							>
								{t("See agenda")}
							</Button>
						</div>
					}
					{renderSessions()}
				</div>
			</div>
			<SessionModal
				addingSession={!!addingSession}
				addingSessionType={addingSession || undefined}
				closeModal={handleCloseModal}
				createSession={createSession}
			/>

			{reorderingSessions ?
				<ReorderContentModal
					isOpen={reorderingSessions}
					onClose={() => { setReorderingSessions(false); }}
					module={module}
					defaultImage={getDefaultAgendaImage(template, 0)}
					isCustomPage={isCustomPage}
					isLandingPage={isLandingPage}
					isHomePage={isHomePage}
					customPageId={customPageId}
				/> : <></>}
			{contentManagement ?
				<ManageAgenda
					page_module={module}
					modalIsOpen={contentManagement}
					setModalClosed={handleCloseManageAgenda}
					openReorderModal={() => setReorderingSessions(true)}
					isCustomPage={isCustomPage}
					isLandingPage={isLandingPage}
					isHomePage={isHomePage}
					customPageId={customPageId}
				/> : <></>}
		</section>
	);
});

EditorAgenda.displayName = 'EditorAgenda';

export default EditorAgenda;
